import { z } from 'zod'
import { DEFAULT_LOCALE, type Locale } from './i18n.ts'

/**
 * The unit master (decision D3).
 *
 * Two ideas do the work here:
 *
 * 1. Every measurable unit belongs to a DIMENSION and carries an exact conversion
 *    factor to that dimension's base unit, so "2 quintal" and "200 kg" are comparable.
 * 2. PACKAGING units (bag, box, coil, bucket...) are counts whose real content is
 *    declared per product via `packQty` + `packUnit` — "1 bag = 50 kg" is data, not a
 *    naming convention. This is why a 4-litre paint "gallon" and a true US gallon
 *    don't have to be the same thing.
 *
 * Factors are stored as integers scaled by 1e6 (`factorMicro`) so conversions stay
 * exact. Every factor below is exact at that scale; none exceed Number.MAX_SAFE_INTEGER.
 */

export const UNIT_DIMENSIONS = ['count', 'mass', 'length', 'area', 'volume'] as const
export type UnitDimension = (typeof UNIT_DIMENSIONS)[number]

export const unitDimensionSchema = z.enum(UNIT_DIMENSIONS)

export const FACTOR_SCALE = 1_000_000

export interface UnitDef {
  code: string
  /** English label, singular. */
  name: string
  /** Nepali label, singular. Read through `unitName`, never directly. */
  nameNe: string
  /** Short form shown next to quantities, e.g. "kg". See `unitSymbol` for Nepali. */
  symbol: string
  dimension: UnitDimension
  /** Size of one unit in dimension base units, x 1e6. */
  factorMicro: number
  /** Decimal places a quantity may carry. Quantities are stored as integers x 10^decimals. */
  decimals: number
  /**
   * A container rather than a measure. Counted in whole numbers; its content is
   * defined per product by `packQty`/`packUnit`.
   */
  packaging?: true
}

/** Base unit of each dimension — the denominator for all conversions. */
export const DIMENSION_BASE: Record<UnitDimension, string> = {
  count: 'pcs',
  mass: 'g',
  length: 'mm',
  area: 'sqmm',
  volume: 'ml',
}

const M = FACTOR_SCALE

export const UNITS: readonly UnitDef[] = [
  // ---- count -------------------------------------------------------------
  {
    code: 'pcs',
    name: 'Piece',
    nameNe: 'थान',
    symbol: 'pcs',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
  },
  {
    code: 'pair',
    name: 'Pair',
    nameNe: 'जोडी',
    symbol: 'pair',
    dimension: 'count',
    factorMicro: 2 * M,
    decimals: 0,
  },
  {
    code: 'dozen',
    name: 'Dozen',
    nameNe: 'दर्जन',
    symbol: 'dz',
    dimension: 'count',
    factorMicro: 12 * M,
    decimals: 0,
  },
  {
    code: 'gross',
    name: 'Gross',
    nameNe: 'ग्रोस',
    symbol: 'gr',
    dimension: 'count',
    factorMicro: 144 * M,
    decimals: 0,
  },
  {
    code: 'hundred',
    name: 'Hundred',
    nameNe: 'सय',
    symbol: '100s',
    dimension: 'count',
    factorMicro: 100 * M,
    decimals: 0,
  },
  {
    code: 'thousand',
    name: 'Thousand',
    nameNe: 'हजार',
    symbol: '1000s',
    dimension: 'count',
    factorMicro: 1000 * M,
    decimals: 0,
  },

  // ---- mass --------------------------------------------------------------
  {
    code: 'g',
    name: 'Gram',
    nameNe: 'ग्राम',
    symbol: 'g',
    dimension: 'mass',
    factorMicro: 1 * M,
    decimals: 0,
  },
  {
    code: 'kg',
    name: 'Kilogram',
    nameNe: 'किलोग्राम',
    symbol: 'kg',
    dimension: 'mass',
    factorMicro: 1_000 * M,
    decimals: 2,
  },
  {
    code: 'quintal',
    name: 'Quintal',
    nameNe: 'क्विन्टल',
    symbol: 'qtl',
    dimension: 'mass',
    factorMicro: 100_000 * M,
    decimals: 2,
  },
  {
    code: 'ton',
    name: 'Metric Ton',
    nameNe: 'टन',
    symbol: 't',
    dimension: 'mass',
    factorMicro: 1_000_000 * M,
    decimals: 3,
  },
  {
    code: 'lb',
    name: 'Pound',
    nameNe: 'पाउन्ड',
    symbol: 'lb',
    dimension: 'mass',
    factorMicro: 453_592_370,
    decimals: 2,
  },

  // ---- length ------------------------------------------------------------
  {
    code: 'mm',
    name: 'Millimetre',
    nameNe: 'मिलिमिटर',
    symbol: 'mm',
    dimension: 'length',
    factorMicro: 1 * M,
    decimals: 0,
  },
  {
    code: 'cm',
    name: 'Centimetre',
    nameNe: 'सेन्टिमिटर',
    symbol: 'cm',
    dimension: 'length',
    factorMicro: 10 * M,
    decimals: 1,
  },
  {
    code: 'm',
    name: 'Metre',
    nameNe: 'मिटर',
    symbol: 'm',
    dimension: 'length',
    factorMicro: 1_000 * M,
    decimals: 2,
  },
  {
    code: 'km',
    name: 'Kilometre',
    nameNe: 'किलोमिटर',
    symbol: 'km',
    dimension: 'length',
    factorMicro: 1_000_000 * M,
    decimals: 3,
  },
  {
    code: 'inch',
    name: 'Inch',
    nameNe: 'इन्च',
    symbol: 'in',
    dimension: 'length',
    factorMicro: 25_400_000,
    decimals: 2,
  },
  {
    code: 'ft',
    name: 'Foot',
    nameNe: 'फिट',
    symbol: 'ft',
    dimension: 'length',
    factorMicro: 304_800_000,
    decimals: 2,
  },
  // Trade term for the linear length of a profile (moulding, timber, gutter).
  {
    code: 'rft',
    name: 'Running Foot',
    nameNe: 'रनिङ फिट',
    symbol: 'rft',
    dimension: 'length',
    factorMicro: 304_800_000,
    decimals: 2,
  },
  {
    code: 'yd',
    name: 'Yard',
    nameNe: 'गज',
    symbol: 'yd',
    dimension: 'length',
    factorMicro: 914_400_000,
    decimals: 2,
  },

  // ---- area --------------------------------------------------------------
  {
    code: 'sqmm',
    name: 'Square Millimetre',
    nameNe: 'वर्ग मिलिमिटर',
    symbol: 'mm²',
    dimension: 'area',
    factorMicro: 1 * M,
    decimals: 0,
  },
  {
    code: 'sqcm',
    name: 'Square Centimetre',
    nameNe: 'वर्ग सेन्टिमिटर',
    symbol: 'cm²',
    dimension: 'area',
    factorMicro: 100 * M,
    decimals: 2,
  },
  {
    code: 'sqm',
    name: 'Square Metre',
    nameNe: 'वर्ग मिटर',
    symbol: 'm²',
    dimension: 'area',
    factorMicro: 1_000_000 * M,
    decimals: 2,
  },
  {
    code: 'sqin',
    name: 'Square Inch',
    nameNe: 'वर्ग इन्च',
    symbol: 'in²',
    dimension: 'area',
    factorMicro: 645_160_000,
    decimals: 2,
  },
  {
    code: 'sqft',
    name: 'Square Foot',
    nameNe: 'वर्ग फिट',
    symbol: 'ft²',
    dimension: 'area',
    factorMicro: 92_903_040_000,
    decimals: 2,
  },

  // ---- volume ------------------------------------------------------------
  {
    code: 'ml',
    name: 'Millilitre',
    nameNe: 'मिलिलिटर',
    symbol: 'ml',
    dimension: 'volume',
    factorMicro: 1 * M,
    decimals: 0,
  },
  {
    code: 'ltr',
    name: 'Litre',
    nameNe: 'लिटर',
    symbol: 'L',
    dimension: 'volume',
    factorMicro: 1_000 * M,
    decimals: 2,
  },
  {
    code: 'cft',
    name: 'Cubic Foot',
    nameNe: 'घन फिट',
    symbol: 'cft',
    dimension: 'volume',
    factorMicro: 28_316_846_592,
    decimals: 2,
  },
  {
    code: 'cum',
    name: 'Cubic Metre',
    nameNe: 'घन मिटर',
    symbol: 'm³',
    dimension: 'volume',
    factorMicro: 1_000_000 * M,
    decimals: 3,
  },

  // ---- packaging ---------------------------------------------------------
  // Counted whole. Content comes from the product's packQty/packUnit.
  {
    code: 'bag',
    name: 'Bag',
    nameNe: 'बोरा',
    symbol: 'bag',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'sack',
    name: 'Sack',
    nameNe: 'सक',
    symbol: 'sack',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'box',
    name: 'Box',
    nameNe: 'बाकस',
    symbol: 'box',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'carton',
    name: 'Carton',
    nameNe: 'कार्टुन',
    symbol: 'ctn',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'crate',
    name: 'Crate',
    nameNe: 'क्रेट',
    symbol: 'crate',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'bundle',
    name: 'Bundle',
    nameNe: 'बन्डल',
    symbol: 'bdl',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'roll',
    name: 'Roll',
    nameNe: 'रोल',
    symbol: 'roll',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'coil',
    name: 'Coil',
    nameNe: 'कुण्डल',
    symbol: 'coil',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'reel',
    name: 'Reel',
    nameNe: 'रिल',
    symbol: 'reel',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'drum',
    name: 'Drum',
    nameNe: 'ड्रम',
    symbol: 'drum',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'bucket',
    name: 'Bucket',
    nameNe: 'बाल्टिन',
    symbol: 'bkt',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'gallon',
    name: 'Gallon (tin)',
    nameNe: 'ग्यालन',
    symbol: 'gal',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'tin',
    name: 'Tin',
    nameNe: 'टिन',
    symbol: 'tin',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'can',
    name: 'Can',
    nameNe: 'क्यान',
    symbol: 'can',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'jar',
    name: 'Jar',
    nameNe: 'जार',
    symbol: 'jar',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'tube',
    name: 'Tube',
    nameNe: 'ट्युब',
    symbol: 'tube',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'packet',
    name: 'Packet',
    nameNe: 'प्याकेट',
    symbol: 'pkt',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'pouch',
    name: 'Pouch',
    nameNe: 'पाउच',
    symbol: 'pouch',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'set',
    name: 'Set',
    nameNe: 'सेट',
    symbol: 'set',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'sheet',
    name: 'Sheet',
    nameNe: 'पाता',
    symbol: 'sheet',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'panel',
    name: 'Panel',
    nameNe: 'प्यानल',
    symbol: 'panel',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'strip',
    name: 'Strip',
    nameNe: 'स्ट्रिप',
    symbol: 'strip',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  // A standard stock length of pipe or rod, e.g. one 6 m GI pipe.
  {
    code: 'length',
    name: 'Length',
    nameNe: 'लम्बाइ',
    symbol: 'len',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'pallet',
    name: 'Pallet',
    nameNe: 'प्यालेट',
    symbol: 'plt',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'truck',
    name: 'Truck Load',
    nameNe: 'ट्रक',
    symbol: 'truck',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'tipper',
    name: 'Tipper Load',
    nameNe: 'टिपर',
    symbol: 'tipper',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
  {
    code: 'trip',
    name: 'Trip',
    nameNe: 'फेरा',
    symbol: 'trip',
    dimension: 'count',
    factorMicro: 1 * M,
    decimals: 0,
    packaging: true,
  },
]

export const UNIT_BY_CODE: ReadonlyMap<string, UnitDef> = new Map(UNITS.map((u) => [u.code, u]))

export const unitCodeSchema = z
  .string()
  .min(1)
  .max(16)
  .refine((c) => UNIT_BY_CODE.has(c), { message: 'Unknown unit' })

export function getUnit(code: string): UnitDef {
  const u = UNIT_BY_CODE.get(code)
  if (!u) throw new Error(`Unknown unit: ${code}`)
  return u
}

export function unitName(code: string, locale: Locale = DEFAULT_LOCALE): string {
  const unit = getUnit(code)
  return locale === 'ne' ? unit.nameNe : unit.name
}

/**
 * The short form beside a number, in the reader's language.
 *
 * Measured units keep their Latin symbol in both: `kg`, `m` and `ltr` are what is
 * stamped on the goods and what a Nepali invoice already prints, so translating
 * them would make the app disagree with the bag in the customer's hand.
 *
 * Counted and packaging units have no such abbreviation — their "symbol" is an
 * English word like `bag` or `roll` — so Nepali gets the word the yard actually
 * uses, बोरा rather than a transliteration of an abbreviation nobody says.
 */
export function unitSymbol(code: string, locale: Locale = DEFAULT_LOCALE): string {
  const unit = getUnit(code)
  if (locale !== 'ne' || unit.dimension !== 'count') return unit.symbol
  return unit.nameNe
}

// ---------------------------------------------------------------------------
// Quantity handling
//
// A quantity is an integer scaled by 10^unit.decimals. 12.75 m is stored as 1275.
// Nothing in the quantity path ever touches a float.
// ---------------------------------------------------------------------------

export function quantityScale(unitCode: string): number {
  return 10 ** getUnit(unitCode).decimals
}

/** Parse user input ("12.75") into the stored integer. Returns null if unparseable. */
export function parseQuantity(input: string, unitCode: string): number | null {
  const trimmed = input.trim()
  if (!/^\d*\.?\d*$/.test(trimmed) || trimmed === '' || trimmed === '.') return null
  const { decimals } = getUnit(unitCode)
  const [whole = '0', frac = ''] = trimmed.split('.')
  if (frac.length > decimals) return null
  const padded = frac.padEnd(decimals, '0')
  const value = Number(whole + padded)
  return Number.isSafeInteger(value) ? value : null
}

/** Render a stored quantity for display, without the unit symbol. */
export function formatQuantity(qty: number, unitCode: string): string {
  const { decimals } = getUnit(unitCode)
  if (decimals === 0) return qty.toLocaleString('en-IN')
  const scale = 10 ** decimals
  const whole = Math.trunc(qty / scale)
  const frac = Math.abs(qty % scale)
    .toString()
    .padStart(decimals, '0')
    .replace(/0+$/, '')
  return frac ? `${whole.toLocaleString('en-IN')}.${frac}` : whole.toLocaleString('en-IN')
}

/** Render a quantity with its unit symbol, e.g. "200 bag" / "200 बोरा". */
export function formatQuantityWithUnit(
  qty: number,
  unitCode: string,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return `${formatQuantity(qty, unitCode)} ${unitSymbol(unitCode, locale)}`
}

/**
 * Convert between units of the same dimension. Returns null across dimensions.
 * Result is rounded to the target unit's precision, so it is for comparison and
 * display only — never feed it back into a price calculation.
 */
export function convertQuantity(qty: number, from: string, to: string): number | null {
  const a = getUnit(from)
  const b = getUnit(to)
  if (a.dimension !== b.dimension) return null
  const inBase = (qty / 10 ** a.decimals) * a.factorMicro
  return Math.round((inBase / b.factorMicro) * 10 ** b.decimals)
}

/**
 * Total content of `qty` selling units, given the product's pack size.
 * 200 bags at 50 kg each -> { qty: 1_000_000 (10000.00 kg scaled), unit: 'kg' }.
 */
export function packContent(
  qty: number,
  sellingUnit: string,
  packQty: number | null,
  packUnit: string | null,
): { qty: number; unit: string } | null {
  if (packQty == null || packUnit == null) return null
  const sellDecimals = getUnit(sellingUnit).decimals
  const sellWhole = qty / 10 ** sellDecimals
  return { qty: Math.round(sellWhole * packQty), unit: packUnit }
}
